/**
 * `AgentToolExecutorModel.ts` — port of Unity's `Executor/Core_Executor.cs`
 * dispatch pipeline.
 *
 * Responsibilities (Unity parity): guardrails authorization first, redacted
 * telemetry, then chain the domain executors until one handles the tool,
 * else `tool_not_supported:{name}`. Executors return plain strings with the
 * `ok` / `ok: …` / `error: …` / `tool_error: …` conventions so results stay
 * compatible with Unity prompts, recorded actions, and the MCP bridge.
 */

import { Emitter } from "@core/events/emitter";
import type { IAIAgentToolsModel, IToolExecutor } from "@ai/registry/AIAgentToolRegistryModel";
import { TOOL_TRIGGER } from "@ai/registry/AIAgentToolRegistryModel";
import type { IAIGuardrailsModel } from "@ai/guardrails/AIGuardrailsModel";
import type { AIConversationPresentationModel } from "@ai/interface/AIConversationPresentationModel";
import type { IAIObservabilityModel } from "@ai/observability/AIObservabilityModel";
import type { UsageDelta } from "@ai/observability/AISessionUsageTracker";
import type { DocumentNameIndex } from "@models/docs/documentDisplayNames";

export type ToolArgs = Record<string, unknown>;

export interface IDomainExecutor {
  /** Return `null` when the tool is not handled by this domain. */
  tryExecute(toolName: string, args: ToolArgs, triggeredBy?: string): Promise<string | null>;
}

/**
 * Orchestrator-owned callbacks the AI-behaviour tools need. Mirrors how
 * Unity executors reach `IAIOrchestratorModel` / `IAIInterfaceModel`
 * without the executor owning session state.
 */
export interface AgentToolExecutorHost {
  sendUserText(text: string): void;
  /** Send a screenshot into the conversation. False when no session. */
  sendUserImage(dataUrl: string): boolean;
  resetConversation(): Promise<boolean>;
  setPersonality(personalityId: string): Promise<boolean>;
  /** Current agent role id (Unity `IAIOrchestratorModel.CurrentRole`). */
  getCurrentRole(): string;
  /** Execute and surface a second registered tool as part of one tool round. */
  executeCompanionToolAsync(
    toolName: string,
    args: ToolArgs,
    triggeredBy: string,
  ): Promise<string>;
  /** Session-scoped role switch — nothing about the selection is stored. */
  setAgentRole(roleId: string): void;
  setConstantListening(enabled: boolean): void;
  /** Open one voice window. False when already always-on or gesture-only on this surface. */
  requestListenUser(): boolean;
  setPanelMinimalMode(hidden: boolean): void;
  setDebugMode(enabled: boolean): void;
  /** Show/hide Spark's 3D head. False when no Spark is mounted. */
  setSparkVisibility(visible: boolean): boolean;
  /**
   * The conversation presentation queue — how a tool surfaces citations, what
   * it searched, and a request to open a document. `null` when no UI is
   * attached (Unity guards on `HasPresentationConsumer` the same way).
   */
  readonly conversationPresentation: AIConversationPresentationModel | null;
  /**
   * Attribute a helper subagent's Responses usage to the session tracker
   * (Unity `TrackHelperAgentUsage`). Optional so a headless host can omit it.
   */
  trackHelperAgentUsage?(source: string, usage: UsageDelta | null): void;
  /**
   * The account's Define-UI document names, indexed for citation labels. A
   * `file_search` annotation carries the uploaded FILE's name, which is the
   * identity the transport matches on and not what a person should read — see
   * `models/docs/documentDisplayNames.ts`. Optional so a headless host can omit
   * it; an absent implementation leaves every citation showing its file name,
   * which is what shipped before this existed.
   */
  loadDocumentNameIndexAsync?(): Promise<DocumentNameIndex>;
}

export class AgentToolExecutorModel implements IToolExecutor {
  /** Fired when a tool mutated the scene/project (Unity `ContextInvalidated`). */
  readonly onContextInvalidated = new Emitter<void>();
  /** Carries the raw args so the orchestrator can drive Spark's gaze
   *  (Unity `ExtractFirstPartFromArgs` → `NotifySparkLookAtPart`). Fires for
   *  EVERY trigger, which is what points Spark at the part an authored
   *  workflow script command is acting on as well as the agent's own. */
  /**
   * `triggeredBy` rides both events so a subscriber can tell WHO ran the
   * tool — the realtime agent ("AI"), or a subagent like the Workflow
   * Builder reviewer, which uses this same executor. Without it the debug
   * feed cannot attribute a card, and a reviewer's context lookup reads as
   * something the user's own agent did.
   */
  readonly onToolExecutionStarted = new Emitter<{
    toolName: string;
    args: ToolArgs;
    triggeredBy: string;
  }>();
  readonly onToolExecutionCompleted = new Emitter<{
    toolName: string;
    result: string;
    triggeredBy: string;
  }>();

  private m_domains: IDomainExecutor[] = [];
  private m_toolsModel: IAIAgentToolsModel | null = null;
  private readonly m_guardrails: IAIGuardrailsModel;
  private readonly m_observability: IAIObservabilityModel;
  private m_sessionId = "";

  constructor(guardrails: IAIGuardrailsModel, observability: IAIObservabilityModel) {
    this.m_guardrails = guardrails;
    this.m_observability = observability;
  }

  /** Wired after construction (registry ↔ executor are co-created). */
  attach(toolsModel: IAIAgentToolsModel, domains: IDomainExecutor[]): void {
    this.m_toolsModel = toolsModel;
    this.m_domains = domains;
  }

  setSessionId(sessionId: string): void {
    this.m_sessionId = sessionId;
  }

  notifyContextInvalidated(): void {
    this.onContextInvalidated.emit();
  }

  async executeAsync(
    toolName: string,
    args: ToolArgs,
    triggeredBy: string = TOOL_TRIGGER.AI,
  ): Promise<string> {
    const tool = this.m_toolsModel?.allTools.find((t) => t.name === toolName) ?? null;

    // `triggeredBy` is part of the authorization question, not just telemetry:
    // the `writerOnly` role gate exempts workflow playback, because an authored
    // script step was authorized by its AUTHOR rather than by the learner
    // watching it. See `AIGuardrailsModel.authorizeToolExecution`.
    const auth = this.m_guardrails.authorizeToolExecution(tool, toolName, triggeredBy);
    if (!auth.isAllowed) {
      return auth.denialMessage ?? "error: permission_denied";
    }

    this.m_observability.trackToolExecution(
      this.m_sessionId,
      toolName,
      Object.keys(args ?? {}),
      triggeredBy,
    );
    this.onToolExecutionStarted.emit({ toolName, args: args ?? {}, triggeredBy });

    let result: string;
    try {
      result = (await this.dispatch(toolName, args ?? {}, triggeredBy))
        ?? `tool_not_supported:${toolName}`;
    } catch (err) {
      result = `error: tool_execution_failed. ${err instanceof Error ? err.message : String(err)}`;
    }

    if (tool?.writerOnly && result.startsWith("ok")) {
      this.notifyContextInvalidated();
    }
    this.onToolExecutionCompleted.emit({ toolName, result, triggeredBy });
    return result;
  }

  private async dispatch(
    toolName: string,
    args: ToolArgs,
    triggeredBy: string,
  ): Promise<string | null> {
    for (const domain of this.m_domains) {
      const result = await domain.tryExecute(toolName, args, triggeredBy);
      if (result !== null) return result;
    }
    return null;
  }
}

// ---------------------------------------------------------------------------
// Arg helpers — ports of Core_Executor's JsonElement helpers.
// ---------------------------------------------------------------------------

export function getString(args: ToolArgs, key: string): string | null {
  const value = args[key];
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

export function getStringList(args: ToolArgs, key: string): string[] {
  const value = args[key];
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === "string")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  const single = getString(args, key);
  return single ? [single] : [];
}

export function getNumber(args: ToolArgs, key: string): number | null {
  const value = args[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function getBool(args: ToolArgs, key: string): boolean | null {
  const value = args[key];
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lower = value.trim().toLowerCase();
    if (lower === "true") return true;
    if (lower === "false") return false;
  }
  return null;
}

export function getVector3(args: ToolArgs, key: string): [number, number, number] | null {
  const value = args[key];
  if (!Array.isArray(value) || value.length < 3) return null;
  const nums = value.map((v) => (typeof v === "number" ? v : Number.parseFloat(String(v))));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  return [nums[0], nums[1], nums[2]];
}
